/**
 * HTTP-транспорт.
 *
 * Пока адрес сервера неизвестен, модуль работает в «отключённом» режиме:
 * `isConfigured()` возвращает `false`, и вызывающий код (движок синхронизации)
 * просто не начинает обмен. Когда появится `VITE_API_URL`, транспорт
 * заработает без единой правки в остальном коде.
 */

import type { z } from 'zod';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** Ошибку сети и 5xx имеет смысл повторить, 4xx — нет. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

const TOKEN_STORAGE_KEY = 'kairo.token';

function getBaseUrl(): string | null {
  const url = import.meta.env.VITE_API_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function isConfigured(): boolean {
  return getBaseUrl() !== null;
}

function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Схема ответа: валидация защищает от расхождения контракта с сервером. */
  schema?: z.ZodType;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * Выполняет запрос к API.
 *
 * Таймаут задан явно: на мобильной сети запрос может «висеть» минутами, и без
 * ограничения интерфейс будет ждать вместе с ним.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const baseUrl = getBaseUrl();
  if (!baseUrl) {
    throw new ApiError('Адрес сервера не настроен', 0, null);
  }

  const { method = 'GET', body, schema, signal, timeoutMs = 15_000 } = options;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

  const token = getToken();

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    const text = await response.text();
    const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

    if (!response.ok) {
      throw new ApiError(
        typeof parsed === 'object' && parsed !== null && 'message' in parsed
          ? String(parsed.message)
          : `Запрос завершился с кодом ${response.status}`,
        response.status,
        parsed,
      );
    }

    return schema ? (schema.parse(parsed) as T) : (parsed as T);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    // Обрыв сети и таймаут неотличимы для fetch — оба дают TypeError/AbortError.
    throw new ApiError(
      error instanceof Error && error.name === 'AbortError'
        ? 'Превышено время ожидания ответа'
        : 'Нет соединения с сервером',
      0,
      null,
    );
  } finally {
    clearTimeout(timeout);
  }
}
